import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control';
import { Flex, Text } from '@chakra-ui/layout';
import { Input } from '@chakra-ui/react';
import type {
  FormState,
  RegisterOptions,
  UseFormRegisterReturn,
  ValidationRule,
} from 'react-hook-form';

import { useIsRevelioAdmin } from '@revelio/auth';

import modalStyles from '../DataBuilder/data-builder-modals.module.css';

export type SnowflakeDbValues = {
  database: string;
  schema: string;
  table: string;
};
type SnowflakeDbField = keyof SnowflakeDbValues;
export type SnowflakeRegister = (
  name: SnowflakeDbField,
  options?: RegisterOptions<SnowflakeDbValues, SnowflakeDbField>
) => UseFormRegisterReturn<string>;
type SnowflakeDbInputsProps = {
  testIdPrefix: string;
  register: SnowflakeRegister;
  errors?: FormState<SnowflakeDbValues>['errors'];
  isOptional?: boolean;
  allowUserDatabase?: boolean;
};
export const SnowflakeDbInputs = ({
  testIdPrefix,
  register,
  errors = {},
  isOptional,
  allowUserDatabase = true,
}: SnowflakeDbInputsProps) => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const databaseValidation: RegisterOptions<SnowflakeDbValues, 'database'> = {};
  const validDBName: { pattern: ValidationRule<RegExp> } = {
    pattern: {
      value: /^[A-Za-z_][A-Za-z0-9_$]*$/,
      message:
        'Names can only contain letters, numbers, underscores and dollar signs. e.g. "my_database"',
    },
  };

  if (!isOptional) {
    databaseValidation.required = 'Database is required';
  }

  if (!allowUserDatabase) {
    databaseValidation.pattern = {
      value: /^(?!user_)/,
      message: 'Delivery to USER databases is not supported',
    };
  }
  const schemaValidation:
    | RegisterOptions<SnowflakeDbValues, 'schema'>
    | undefined = isOptional
    ? undefined
    : {
        required: 'Schema is required',
      };
  const tableValidation: RegisterOptions<SnowflakeDbValues, 'table'> =
    isOptional
      ? {}
      : {
          required: 'Table is required',
          ...validDBName,
        };
  return (
    <Flex alignItems="flex-end">
      {!!isRevelioAdmin && (
        <>
          <FormControl
            mr="2"
            isInvalid={!!errors.database}
            maxW="175px"
            h="72px"
          >
            <FormLabel
              className={modalStyles.modalInkText}
              color="text.primary"
            >
              Database
            </FormLabel>

            <Input
              className={modalStyles.modalInput}
              data-testid={`${testIdPrefix}-database-input`}
              placeholder="e.g. database"
              {...register('database', databaseValidation)}
            />
            <FormErrorMessage>
              {errors.database && errors.database.message}
            </FormErrorMessage>
          </FormControl>

          <Text mr="2" fontSize="35px" lineHeight="none">
            .
          </Text>

          <FormControl mr="2" isInvalid={!!errors.schema} maxW="175px" h="72px">
            <FormLabel
              className={modalStyles.modalInkText}
              color="text.primary"
            >
              Schema
            </FormLabel>
            <Input
              className={modalStyles.modalInput}
              data-testid={`${testIdPrefix}-schema-input`}
              placeholder="e.g. schema"
              {...register('schema', schemaValidation)}
            />
            <FormErrorMessage>
              {errors.schema && errors.schema.message}
            </FormErrorMessage>
          </FormControl>

          <Text mr="2" fontSize="35px" lineHeight="none">
            .
          </Text>
        </>
      )}

      <FormControl mr="2" isInvalid={!!errors.table} h="72px">
        <FormLabel className={modalStyles.modalInkText} color="text.primary">
          Table
        </FormLabel>
        <Input
          className={modalStyles.modalInput}
          data-testid={`${testIdPrefix}-table-input`}
          placeholder="e.g. my_table"
          {...register('table', tableValidation)}
        />
        <FormErrorMessage>
          {errors.table && errors.table.message}
        </FormErrorMessage>
      </FormControl>
    </Flex>
  );
};
